import React, { useMemo } from 'react'
import { View } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import moment from 'moment'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Icon, Text } from '@components/base'
import {
  AppStackParamList,
  CommonAuthorizedStackParamList,
  VideoCallProduct,
} from '@src/navigation/types'
import { OrderSummary } from '@src/screens/Marketplace/components/checkout/OrderSummary'
import { ORDER_SUMMARY_DATE_FORMAT } from '@src/config/momentFormat'
import { PaymentMethodCard } from '@src/screens/Marketplace/components/checkout/PaymentMethodCard'
import { EhrAppointmentAttendanceChargeKind, InsurancePolicy, PaymentMethod } from '@src/types'
import {
  PAYMENT_MISSING_TITLE,
  DEFAULT_PAYMENT_BODY,
  DEFAULT_PAYMENT_TITLE,
  ALLOWANCE_COVERED_BODY,
  ALLOWANCE_COVERED_TITLE,
  INSURANCE_COVERED_PAYMENT_BODY,
  INSURANCE_COVERED_PAYMENT_TITLE,
  INSURANCE_IN_REVIEW_DISCLAIMER,
  INSURANCE_IN_REVIEW_PAYMENT_BODY,
  INSURANCE_IN_REVIEW_PAYMENT_TITLE,
  SELF_PAY_PAYMENT_TITLE,
  SELF_PAY_PAYMENT_BODY,
  SELF_PAY_MISSING_PAYMENT_BODY,
} from './constants'

interface PaymentSummaryProps {
  attendanceChargeKind: EhrAppointmentAttendanceChargeKind
  date: string
  product: VideoCallProduct | null | undefined
  insurancePolicy: InsurancePolicy | null
  paymentMethod: Pick<PaymentMethod, 'expirationDate' | 'last4' | 'cardType'> | null | undefined
  onlyShowDateCharged?: boolean
}

export const PaymentSummary = ({
  attendanceChargeKind,
  date,
  product,
  insurancePolicy,
  paymentMethod,
  onlyShowDateCharged = false,
}: PaymentSummaryProps) => {
  const styles = useStyleSheet(themedStyles)
  const navigation = useNavigation<
    StackNavigationProp<AppStackParamList & CommonAuthorizedStackParamList>
  >()

  const allowanceCovered =
    attendanceChargeKind === EhrAppointmentAttendanceChargeKind.BsAllowanceCovered
  const insuranceCovered =
    !!insurancePolicy?.eligible &&
    attendanceChargeKind === EhrAppointmentAttendanceChargeKind.BsInsuranceCovered
  const insuranceInReview =
    !!insurancePolicy?.inReview &&
    attendanceChargeKind === EhrAppointmentAttendanceChargeKind.BsInsuranceCovered
  const selfPay = attendanceChargeKind === EhrAppointmentAttendanceChargeKind.BsSelfPay
  const showPaymentMethod = allowanceCovered || !insuranceCovered || !paymentMethod
  const showAllowanceCoveredSummary = allowanceCovered && !!product
  const showOrderSummary = selfPay && !!product

  const { disclaimer, paymentTitle, paymentBody } = useMemo(() => {
    if (allowanceCovered) {
      return {
        paymentTitle: paymentMethod ? ALLOWANCE_COVERED_TITLE : PAYMENT_MISSING_TITLE,
        paymentBody: ALLOWANCE_COVERED_BODY,
      }
    }
    if (insuranceInReview) {
      return {
        disclaimer: INSURANCE_IN_REVIEW_DISCLAIMER,
        paymentTitle: paymentMethod ? INSURANCE_IN_REVIEW_PAYMENT_TITLE : PAYMENT_MISSING_TITLE,
        paymentBody: INSURANCE_IN_REVIEW_PAYMENT_BODY,
      }
    }
    if (insuranceCovered) {
      return {
        paymentTitle: paymentMethod ? INSURANCE_COVERED_PAYMENT_TITLE : PAYMENT_MISSING_TITLE,
        paymentBody: INSURANCE_COVERED_PAYMENT_BODY,
      }
    }
    if (selfPay) {
      return {
        paymentTitle: paymentMethod ? SELF_PAY_PAYMENT_TITLE : PAYMENT_MISSING_TITLE,
        paymentBody: paymentMethod ? SELF_PAY_PAYMENT_BODY : SELF_PAY_MISSING_PAYMENT_BODY,
      }
    }
    return {
      paymentTitle: DEFAULT_PAYMENT_TITLE,
      paymentBody: DEFAULT_PAYMENT_BODY,
    }
  }, [insuranceCovered, insuranceInReview, selfPay, allowanceCovered, paymentMethod])

  const chargeDate = useMemo(() => moment(date).format(ORDER_SUMMARY_DATE_FORMAT), [date])

  const onEditPaymentMethodPress = () => {
    navigation.navigate('CreatePaymentMethod')
  }

  if (!showOrderSummary && !showAllowanceCoveredSummary && !showPaymentMethod) {
    return null
  }

  return (
    <View style={styles.container}>
      {showAllowanceCoveredSummary && (
        <OrderSummary
          title="Booking Summary"
          price={product.price}
          estimatedTax={0}
          discount={product.price}
          discountLabel="Credits to be used"
          discountDisplayValue="1"
          chargeDate={chargeDate}
          showEstimatedTax={false}
          onlyShowDateCharged
        />
      )}
      {showOrderSummary && (
        <OrderSummary
          title="Booking Summary"
          price={product.price}
          estimatedTax={0}
          discount={0}
          chargeDate={chargeDate}
          disclaimer={disclaimer}
          onlyShowDateCharged={onlyShowDateCharged}
        />
      )}
      {showPaymentMethod && (
        <View>
          {(showOrderSummary || showAllowanceCoveredSummary) && <View style={styles.divider} />}
          <View
            style={[
              styles.paymentTitle,
              !showOrderSummary &&
                !showAllowanceCoveredSummary &&
                styles.paymentTitleWithoutDivider,
            ]}
          >
            {!paymentMethod && (
              <Icon name="warning-circle" style={styles.paymentIcon} weight="bold" />
            )}
            <Text type="title-3">{paymentTitle}</Text>
          </View>
          <Text type="regular" style={styles.paymentBody}>
            {paymentBody}
          </Text>
          <PaymentMethodCard
            paymentMethod={paymentMethod}
            onEditPaymentMethodPress={onEditPaymentMethodPress}
            hideTitle
          />
          <View style={styles.divider} />
        </View>
      )}
    </View>
  )
}

const themedStyles = StyleService.create({
  container: {
    marginBottom: 16,
  },
  paymentTitle: {
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  paymentTitleWithoutDivider: {
    marginTop: 16,
  },
  paymentIcon: {
    top: -1,
    width: 20,
    height: 20,
    color: 'theme.warning.base',
  },
  paymentBody: {
    marginBottom: 16,
  },
  divider: {
    marginVertical: 16,
    marginHorizontal: -16,
    height: 8,
    backgroundColor: 'theme.surface.base2',
  },
})
